import { useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const ICON = { success: CheckCircle2, warning: AlertTriangle, error: XCircle, info: Info };
const TINT = {
  success: "text-[#00E676]",
  warning: "text-[#FFD600]",
  error: "text-[#FF3B30]",
  info: "text-[#00F0FF]",
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadUnread = () => api.get("/notifications/unread-count").then(({ data }) => setUnread(data.unread_count || 0)).catch(() => {});
  const loadAll = () => api.get("/notifications", { params: { limit: 30 } }).then(({ data }) => {
    setItems(data.items || []);
    setUnread(data.unread_count || 0);
  }).catch(() => {});

  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (open) loadAll();
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAllRead = async () => {
    try { await api.post("/notifications/mark-all-read"); setUnread(0); loadAll(); } catch {}
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notif_id}/read`); } catch {}
    }
    setOpen(false);
    if (n.action_url) {
      if (n.action_url.startsWith("http")) window.open(n.action_url, "_blank");
      else navigate(n.action_url);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-gray-300 hover:text-white transition-colors"
        aria-label="Notifications">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-[9px] text-white flex items-center justify-center font-medium">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown"
             className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-[#0F0F14] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <p className="text-sm text-white font-medium">Notifications</p>
            {items.length > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAllRead}
                      className="text-xs text-[#00F0FF] hover:text-white flex items-center gap-1">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[460px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No notifications yet.</p>
            ) : (
              items.map((n) => {
                const Icon = ICON[n.type] || Info;
                return (
                  <button
                    key={n.notif_id}
                    onClick={() => openItem(n)}
                    data-testid={`notif-item-${n.notif_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${!n.read ? "bg-[#7000FF]/5" : ""}`}>
                    <div className="flex gap-3">
                      <Icon size={16} className={`${TINT[n.type] || TINT.info} shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-white truncate">{n.title}</p>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#7000FF] mt-1.5 shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-600 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
